'use client';
import { faPrint, faX } from '@fortawesome/pro-regular-svg-icons';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalContent, Select } from '@pickleballinc/react-ui';
import { useUser } from '@pickleballinc/ui/components/global/UserInfoProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import Switch from '@/components/Switch';
import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import { useRemoveRatingPlayer } from '@/modules/league/new-components/matches-components/hooks';
import { BenchCard } from '@/modules/league/new-components/matches-components/MatchesComponent/BenchCard';
import { MatchCard } from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/MatchCard';
import PrintScoreSheetModal from '@/modules/league/new-components/matches-components/MatchesComponent/PrintForm/PrintScoreSheetModal';
import {
  GameDayInfo,
  MatchesResult,
  MatchGroup,
  PlayersOnBench,
  Round,
  SequenceButtons,
} from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';

export interface MatchesComponentProps {
  headerContent: GameDayInfo;
  isAuthorized: boolean | null;
  userUuid: string | undefined;
  matchesResult: MatchesResult;
  rounds: { sequences: Round[] };
  benchGroup: { ratingSessionPlayersOnBench: PlayersOnBench[] };
  allPlayersForRemove: { allRatingSessionPlayers: PlayersOnBench[] };
  sequenceStats: { sequenceStats: SequenceButtons[] };
}

const MatchesComponent = ({
  headerContent,
  isAuthorized,
  userUuid,
  matchesResult,
  rounds,
  benchGroup,
  allPlayersForRemove,
}: MatchesComponentProps) => {
  const userData = useUser();
  const { details } = headerContent;
  const { matchGroups, matches } = matchesResult;
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const router = useRouter();
  const newSearchParams = new URLSearchParams(searchParams?.toString());
  const selectedMatchGroup = newSearchParams.get('matchGroup');
  const firstMatchGroups = selectedMatchGroup
    ? matchGroups.find((matchGroup) => matchGroup.PoolID === selectedMatchGroup)
    : details.rankingType === 'UpDown'
      ? matchGroups[0]
      : matchGroups.find((matchGroup) => matchGroup.PoolID === matches[0]?.PoolID);
  const [matchGroup, setMatchGroup] = useState<MatchGroup | undefined>(
    firstMatchGroups || matchGroups[0]
  );

  const sequenceNumber = newSearchParams.get('sequenceNumber');
  const currentRound = sequenceNumber
    ? rounds.sequences.find((round) => round.SequenceNumber === parseInt(sequenceNumber))
    : rounds.sequences.find((round) => round.SequenceNumber === matchGroups[0]?.SequenceNumber);
  const [round, setRound] = useState<Round | undefined>(currentRound);
  const [showMessage, setShowMessage] = useState(false);
  const [showMessageGroup, setShowMessageGroup] = useState(false);
  const [showPrintScoreSheets, setShowPrintScoreSheets] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState(
    allPlayersForRemove?.allRatingSessionPlayers?.map((player) => ({
      SessionTeamID: player.SessionTeamID,
      IsChecked: player.IsRemoved ? 1 : 0,
    }))
  );
  const isPartnerLeague = details.PlayerTypeTitle === 'Partner' ? 'Teams' : 'Players';
  const { setLoading } = useLoader();
  // State to store players and their "IsRemoved" status
  const [playersOnBench, setPlayersOnBench] = useState<PlayersOnBench[]>(
    allPlayersForRemove?.allRatingSessionPlayers || []
  );
  // Toggle player selection based on the switch
  const handleSwitchToggle = (player: PlayersOnBench) => {
    // Update the IsRemoved property for the player in the main state
    setPlayersOnBench((prevPlayers) =>
      prevPlayers.map((p) =>
        p.SessionTeamID === player.SessionTeamID
          ? { ...p, IsRemoved: !p.IsRemoved } // Toggle the IsRemoved value
          : p
      )
    );

    // Update selectedPlayers array with only SessionTeamID and IsChecked (1 or 0)
    setSelectedPlayers((prevSelectedPlayers) =>
      prevSelectedPlayers.map((p) =>
        p.SessionTeamID === player.SessionTeamID
          ? { ...p, IsChecked: player.IsRemoved ? 0 : 1 } // Toggle IsChecked based on player.IsRemoved
          : p
      )
    );
  };
  const { mutate: removeRatingPlayer } = useRemoveRatingPlayer();
  // Handle Save action
  const handleSave = () => {
    // Do something with the selectedPlayers array
    setLoading(true);
    const fullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`;
    removeRatingPlayer(
      { matchDateId: details.MatchDateID, sequenceNumber: 1, fullName, jsonPost: selectedPlayers },
      {
        onSuccess: (data) => {
          if (data.message) {
            toast.success(data.message);
          }
          queryClient.invalidateQueries(['league_matches']);
          setLoading(false);
        },
        onError: () => {
          toast.error('Failed to remove a player.');
          setLoading(false);
        },
      }
    );
    // Example: You could make an API call here with the selected players
    setModalOpened(false);
  };
  useEffect(() => {
    if (round && details.isRatingLeague) {
      // Set default URL params with the updated round value
      newSearchParams.set('sequenceNumber', round.SequenceNumber.toString());
      // Replace URL with updated params
      router.replace(`?${newSearchParams.toString()}`, { scroll: false });
    }
  }, [round, details.isRatingLeague, router]);
  useEffect(() => {
    if (round && details.isRatingLeague) {
      setRound(currentRound);
      setSelectedOptionRound(currentRound);
    }
  }, [currentRound]);

  const myGroup = newSearchParams.get('myGroup');

  useEffect(() => {
    if (details.UserIsInGroup && details.CurrentUser_PoolID && myGroup) {
      setSelectedOption(firstMatchGroups);
      setMatchGroup(firstMatchGroups);
      newSearchParams.set('matchGroup', details.CurrentUser_PoolID);
      newSearchParams.delete('myGroup');
      router.replace(`?${newSearchParams.toString()}`, { scroll: false });
      router.refresh();
    }
  }, [firstMatchGroups, router, matchGroup]);

  useEffect(() => {
    if (matchGroup && !details.isRatingLeague) {
      newSearchParams.set('matchGroup', matchGroup.PoolID.toString());
      router.replace(`?${newSearchParams.toString()}`, { scroll: false });
    }
  }, [matchGroup, router, matchGroups, selectedMatchGroup]);

  useEffect(() => {
    if (
      details.rankingType === 'UpDown' &&
      details.numberOfSequences > 1 &&
      details.DidGenerate2ndSequence
    ) {
      // Set default URL params with the updated sequence value
      setMatchGroup(matchGroups[0]);
      setSelectedOption(matchGroups[0]);
    }
  }, [sequenceNumber]);

  const allDataLoaded = !!details && matchGroups.length > 0 && matches.length > 0;

  useEffect(() => {
    setLoading(!allDataLoaded);
  }, [selectedMatchGroup, sequenceNumber, myGroup, matches]);

  const buttonClasses =
    '!md:text-sm rounded-md border border-gray-300 !bg-white !p-1 !text-xs font-semibold tracking-wider hover:!text-gray-900 shadow-xs';
  const [selectedOption, setSelectedOption] = useState<MatchGroup | undefined>(matchGroup);
  const [selectedOptionRound, setSelectedOptionRound] = useState<Round | undefined>(round);
  const options = matchGroups.map((item) => ({
    value: item.PoolID.toString(),
    label: `Group ${item.PoolNumber} : ${item.NumberOfTeamsInPool} ${isPartnerLeague}, ${item.TotalReminaingGames} Matches Remaining`,
    originalItem: item,
  }));
  const optionsRounds = rounds.sequences.map((item) => ({
    value: item.SequenceNumber.toString(),
    label: `Round ${item.SequenceNumber}`,
    originalItem: item,
  }));

  type MatchGroupOption = {
    value: string;
    label: string;
    originalItem: MatchGroup;
  };
  interface RoundOption {
    value: string;
    label: string;
    originalItem: Round; // replace `YourRoundType` with the actual type
  }

  return (
    <>
      <div className='mx-auto w-full p-2 md:px-3 lg:px-3'>
        {isAuthorized && matches && details.isMatchStarted && !details.DateLockScores ? (
          <div className='flex items-end justify-end px-1 pb-2'>
            <Button
              variant='primary'
              onClick={() => {
                setShowPrintScoreSheets(true);
              }}
              className='!md:text-sm max-h-[38px] rounded-md !border-leagues-700 !bg-leagues-700 !text-xs font-semibold tracking-wider !text-white hover:!bg-leagues-500'
            >
              <div className='flex items-center justify-center gap-2 px-1 py-[6px]'>
                <FontAwesomeIcon icon={faPrint} />
                <span>Print Score Sheets</span>
              </div>
            </Button>
          </div>
        ) : null}
        {(matches && details.PlayerTypeTitle === 'Partner' && matchGroups.length !== 1) ||
        (matchGroups?.length !== 1 && !details.isRatingLeague) ? (
          <div className='flex flex-col gap-2 px-1 md:flex-row md:px-0'>
            <div className='w-full md:w-1/2 lg:w-1/3'>
              <Select
                options={options}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderColor: state.isFocused ? '#f63d68' : '#ccc',
                    boxShadow: state.isFocused ? '0 0 0 1px #f63d68' : 'none',
                    '&:hover': {
                      borderColor: '#f63d68',
                    },
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#f63d68B3'
                      : state.isFocused
                        ? '#f63d6833'
                        : 'white',
                  }),
                }}
                onChange={(newValue: unknown) => {
                  const option = newValue as MatchGroupOption | null;
                  if (!option) return;
                  setLoading(true);
                  const selectedMatchGroup = option.originalItem;
                  setSelectedOption(selectedMatchGroup);
                  setMatchGroup(selectedMatchGroup);
                  queryClient.invalidateQueries(['league_game_day_header']);
                  queryClient.invalidateQueries(['league_matches']);
                }}
                value={
                  selectedOption
                    ? {
                        value: selectedOption.PoolID.toString(),
                        label: `Group ${selectedOption.PoolNumber} : ${selectedOption.NumberOfTeamsInPool} ${isPartnerLeague}, ${selectedOption.TotalReminaingGames} Matches Remaining`,
                      }
                    : null
                }
              />
            </div>
          </div>
        ) : !details.isRatingLeague ? (
          <div className='flex items-center pl-2 text-sm font-semibold text-gray-900 md:w-full md:text-base'>
            {matchGroups.map((item: MatchGroup) => {
              const { PoolNumber, NumberOfTeamsInPool, TotalReminaingGames } = item;
              const groupInfo = details.traditionalLeague
                ? `${NumberOfTeamsInPool} ${isPartnerLeague}`
                : `Group ${PoolNumber} : ${NumberOfTeamsInPool} ${isPartnerLeague}, ${TotalReminaingGames} Matches Remaining`;

              return groupInfo;
            })}
          </div>
        ) : (
          <div className='flex flex-row justify-between gap-2 px-1 md:px-0'>
            <div className='w-36'>
              <Select
                options={optionsRounds}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderColor: state.isFocused ? '#f63d68' : '#ccc',
                    boxShadow: state.isFocused ? '0 0 0 1px #f63d68' : 'none',
                    '&:hover': {
                      borderColor: '#f63d68',
                    },
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#f63d68B3'
                      : state.isFocused
                        ? '#f63d6833'
                        : 'white',
                  }),
                }}
                onChange={(newValue: unknown) => {
                  const option = newValue as RoundOption | null;
                  if (!option) return;
                  setLoading(true);
                  const selectedRound = option.originalItem;
                  setSelectedOptionRound(selectedRound);
                  setRound(selectedRound);
                  queryClient.invalidateQueries(['league_game_day_header']);
                  queryClient.invalidateQueries(['league_matches']);
                }}
                value={
                  selectedOptionRound
                    ? {
                        value: selectedOptionRound.SequenceNumber.toString(),
                        label: `Round ${selectedOptionRound.SequenceNumber}`,
                      }
                    : null
                }
              />
            </div>
            {userUuid &&
            isAuthorized &&
            round?.SequenceNumber ===
              rounds.sequences[rounds.sequences.length - 1]?.SequenceNumber ? (
              <Button
                variant='link-gray'
                className={`${buttonClasses} !min-w-28 p-1 !text-leagues-500`}
                onClick={() => {
                  setModalOpened(true);
                }}
              >
                <div className='p-1'>Remove Player</div>
              </Button>
            ) : null}
          </div>
        )}
        {matchGroup?.NumberOfTeamsInPool === 6 && details.PlayerTypeID === 1 ? (
          <button
            className='ml-2 mr-1 mt-4 flex text-xs font-bold text-leagues-500 hover:underline focus:outline-none'
            type='button'
            onClick={() => {
              setShowMessage(true);
            }}
          >
            <FontAwesomeIcon className='mt-[0.1rem]' icon={faQuestionCircle} size='xl' />
            <span className='ml-2 mt-[0.1rem] text-sm md:mt-[0.05rem] md:text-base'>
              {' '}
              Why am I playing with 1 player twice?
            </span>
          </button>
        ) : matchGroup?.NumberOfTeamsInPool === 7 && details.PlayerTypeID === 1 ? (
          <button
            className='ml-2 mr-1 mt-4 flex text-xs font-bold text-leagues-500 hover:underline focus:outline-none'
            type='button'
            onClick={() => {
              setShowMessageGroup(true);
            }}
          >
            <FontAwesomeIcon className='mt-[0.1rem]' icon={faQuestionCircle} size='xl' />
            <span className='ml-2 mt-[0.1rem] text-sm md:mt-[0.05rem] md:text-base'>
              {' '}
              Why do I only play 5 games?
            </span>
          </button>
        ) : null}
        {details.leagueTypeId === 2 ? (
          <span className='line-clamp-2 px-2 text-left text-sm font-semibold text-error-500 md:text-base'>
            Players can edit scores up to 24 hours after finishing a match.
          </span>
        ) : null}
        <div className='grid grid-cols-1 gap-2 p-1 py-2 md:grid-cols-2 md:gap-2 md:p-0 md:py-2 lg:grid-cols-3 lg:gap-6'>
          <MatchCard
            matchesResult={matchesResult}
            headerContent={headerContent}
            isAuthorized={isAuthorized}
            userUuid={userUuid}
          />
        </div>
        {details.isRatingLeague &&
        benchGroup &&
        benchGroup.ratingSessionPlayersOnBench.length > 0 ? (
          <div className='grid grid-cols-1 p-1 md:p-0 md:py-2'>
            <BenchCard benchGroup={benchGroup} />
          </div>
        ) : null}
      </div>
      {isAuthorized && showPrintScoreSheets ? (
        <PrintScoreSheetModal
          open={showPrintScoreSheets}
          onClose={() => setShowPrintScoreSheets(false)}
          matchesResult={matchesResult}
          headerContent={headerContent}
        />
      ) : null}
      <ConfirmationModal
        open={showMessage}
        onClose={() => setShowMessage(false)}
        onConfirm={() => {
          setShowMessage(false);
        }}
        title='Why am I partnering with 1 player twice?'
        message='In a 6 player scramble you partner with everyone once and 1 player you partner with twice. This allows all players in the group to partner with each other once and still play an equal number of games.'
        doNotShowCloseButton
      />
      <ConfirmationModal
        open={showMessageGroup}
        onClose={() => setShowMessageGroup(false)}
        onConfirm={() => {
          setShowMessageGroup(false);
        }}
        title='Why am I only playing with 5 other players?'
        message='With a group of 7, players get 5 games each. This creates an even number of games for all.'
        doNotShowCloseButton
      />
      <Modal open={modalOpened} onOpenChange={() => setModalOpened(false)}>
        <ModalContent className='flex h-auto max-h-[550px] w-[calc(100%-16px)] flex-col gap-4 !p-6 !outline-none md:max-h-[650px] md:max-w-[500px]'>
          <div className='flex flex-col gap-4 !bg-white'>
            <div className='relative'>
              <h2 className='pr-4 text-left text-lg font-medium leading-[22px] text-gray-900'>
                Remove a Player
              </h2>
              <div
                className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
                onClick={() => {
                  setModalOpened(false);
                }}
              >
                <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
              </div>
            </div>
            <div className='flex w-full justify-center'>
              <div className='flex w-full justify-center text-sm md:text-base'>
                Select all players being removed and click the Save button.
              </div>
            </div>
          </div>
          <div className='flex w-full flex-col justify-between overflow-auto'>
            <div className='items-left flex flex-col gap-4'>
              {playersOnBench.map((item) => {
                return (
                  <div key={`${item.FirstName}_${item.LastName}`} className='flex flex-row'>
                    <Switch
                      defaultChecked={item.IsRemoved}
                      onClick={() => handleSwitchToggle(item)}
                      key={item.SessionTeamID}
                      borderColor={'border-leagues-300/90'}
                      bgColor={'bg-leagues-300/90'}
                    />
                    <label className='ml-2 text-sm md:text-base'>
                      {item.FirstName} {item.LastName}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
          <div className='flex items-center justify-between gap-4'>
            <Button
              variant='link-gray'
              className='flex w-[200px] justify-center !border !border-gray-300 !px-4 !py-2.5 text-[16px] text-sm !text-gray-500 focus:!shadow-[unset]'
              onClick={() => {
                setModalOpened(false);
              }}
            >
              Close
            </Button>
            <Button
              variant='primary'
              className='flex w-[200px] justify-center !px-4 !py-2.5 text-[16px] focus:!shadow-[unset]'
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MatchesComponent;
